import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Card, Spin, Table, Tag } from '@/shared/ui/primitives';
import { useDemandIntelligence } from '../hooks/useAnalyticsQueries';
import type { GrowthAnalyticsParams } from '../api/analytics.api';
import AnalyticsFilterBar from '../components/AnalyticsFilterBar';

type DistrictRow = {
  districtName?: string;
  cityName?: string;
  searchCount?: number;
  districtSelectionCount?: number;
  zeroResultCount?: number;
  nearbyFallbackCount?: number;
  contactCompletedCount?: number;
  activeListingCount?: number;
  zeroResultRate?: number;
  nearbyFallbackRate?: number;
  contactConversionRate?: number;
  demandSupplyRatio?: number;
  currentDemandCount?: number;
  previousDemandCount?: number;
  growthCount?: number;
  title?: string;
  message?: string;
  priority?: string;
};

function defaultFrom() {
  const date = new Date();
  date.setDate(date.getDate() - 30);
  return date.toISOString().slice(0, 10);
}

function pct(value: any) {
  const n = typeof value === 'number' ? value : Number(value ?? 0);
  return `${Math.round(n * 1000) / 10}%`;
}

function number(value: any) {
  const n = Number(value ?? 0);
  return Number.isFinite(n) ? n.toLocaleString() : '0';
}

function signedNumber(value: any) {
  const n = Number(value ?? 0);
  if (!Number.isFinite(n)) return '0';
  return n > 0 ? `+${n.toLocaleString()}` : n.toLocaleString();
}

function cleanName(value: any, fallback = 'Unmapped district') {
  const text = String(value ?? '').trim();
  if (!text || /^\d+$/.test(text) || ['unknown', 'null', 'undefined'].includes(text.toLowerCase())) return fallback;
  return text;
}

function districtName(row?: DistrictRow, fallback = 'Unmapped district') {
  return cleanName(row?.districtName, fallback);
}

function cityName(row?: DistrictRow) {
  return cleanName(row?.cityName, '');
}

function demandSignals(row?: DistrictRow) {
  return Number(row?.searchCount ?? 0) + Number(row?.districtSelectionCount ?? 0);
}

function growthSignals(row?: DistrictRow) {
  return Number(row?.growthCount ?? 0);
}

function businessRows(rows?: DistrictRow[]) {
  return (Array.isArray(rows) ? rows : [])
    .filter((row) => districtName(row) !== 'Unmapped district')
    .filter((row) => demandSignals(row) > 0 || Number(row.zeroResultCount ?? 0) > 0 || Number(row.nearbyFallbackCount ?? 0) > 0 || Number(row.contactCompletedCount ?? 0) > 0);
}

function trendRows(rows?: any[]) {
  return Array.isArray(rows) ? rows.slice(-14) : [];
}

function Stat({ title, value, hint }: { title: string; value: any; hint?: string }) {
  return (
    <div className="ds-stat">
      <div className="ds-stat-head"><span>{title}</span></div>
      <div className="ds-stat-value">{value ?? 0}</div>
      {hint && <div className="ds-stat-hint">{hint}</div>}
    </div>
  );
}

function MetricGrid({ children }: { children: React.ReactNode }) {
  return <div className="ds-row" style={{ gap: 16, marginInline: -8 }}>{children}</div>;
}

function MetricCell({ children }: { children: React.ReactNode }) {
  return <div style={{ flex: '1 1 220px', padding: 8 }}>{children}</div>;
}

function InsightCard({ insight }: { insight: any }) {
  const { t } = useTranslation();
  const color = insight?.tone === 'positive' ? 'success' : insight?.tone === 'high' ? 'error' : 'warning';
  return (
    <Card styles={{ body: { padding: 16 } }} style={{ flex: '1 1 280px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
        <strong>{insight?.title}</strong>
        <Tag color={color}>{String(t(`analytics.demand.tone.${insight?.tone ?? 'insight'}`, insight?.tone ?? 'insight'))}</Tag>
      </div>
      <div className="ds-muted" style={{ marginTop: 8, lineHeight: 1.5 }}>{insight?.message}</div>
    </Card>
  );
}

function DistrictRankCard({ title, rows, metric, renderMetric, emptyText }: {
  title: string;
  rows?: DistrictRow[];
  metric: keyof DistrictRow | 'demandSignals' | 'growthSignals';
  renderMetric?: (value: any) => string;
  emptyText?: string;
}) {
  const { t } = useTranslation();
  const list = businessRows(rows).slice(0, 6);
  return (
    <Card title={title} style={{ flex: '1 1 360px' }}>
      {list.length === 0 ? (
        <div className="ds-muted">{emptyText ?? t('analytics.demand.noMappedDistricts')}</div>
      ) : (
        <div style={{ display: 'grid', gap: 10 }}>
          {list.map((row, index) => (
            <div
              key={`${title}-${row.districtName}-${index}`}
              style={{ display: 'grid', gridTemplateColumns: '32px 1fr auto', gap: 10, alignItems: 'center' }}
            >
              <div style={{ fontWeight: 700, color: 'var(--ds-primary)' }}>{index + 1}</div>
              <div>
                <div style={{ fontWeight: 650 }}>{districtName(row, t('analytics.demand.unmappedDistrict'))}</div>
                {cityName(row) ? <div className="ds-muted">{cityName(row)}</div> : null}
              </div>
              <strong>{renderMetric ? renderMetric(metric === 'demandSignals' ? demandSignals(row) : metric === 'growthSignals' ? growthSignals(row) : row[metric]) : number(metric === 'demandSignals' ? demandSignals(row) : metric === 'growthSignals' ? growthSignals(row) : row[metric])}</strong>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function RecommendationList({ rows }: { rows?: DistrictRow[] }) {
  const { t } = useTranslation();
  const list = (Array.isArray(rows) ? rows : [])
    .filter((row) => districtName(row, t('analytics.demand.unmappedDistrict')) !== t('analytics.demand.unmappedDistrict'))
    .filter((row) => !String(row.message ?? '').match(/\b(null|undefined|unknown)\b/i))
    .slice(0, 8);
  return (
    <Card title={t('analytics.demand.recommendations')} style={{ marginTop: 16 }}>
      {list.length === 0 ? (
        <div className="ds-muted">{t('analytics.demand.noRecommendations')}</div>
      ) : (
        <div style={{ display: 'grid', gap: 12 }}>
          {list.map((row, index) => (
            <div key={`${row.title}-${row.districtName}-${index}`} style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'start' }}>
              <div>
                <strong>{row.title}</strong>
                <div className="ds-muted" style={{ marginTop: 4 }}>{row.message}</div>
              </div>
              <Tag color={row.priority === 'high' ? 'error' : row.priority === 'positive' ? 'success' : 'warning'}>
                {row.priority}
              </Tag>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function TrendBars({ rows }: { rows?: any[] }) {
  const { t } = useTranslation();
  const list = trendRows(rows);
  const max = Math.max(1, ...list.map((row) => Number(row.searchCount ?? 0)));
  return (
    <Card title={t('analytics.demand.trend')} style={{ marginTop: 16 }}>
      {list.length === 0 ? (
        <div className="ds-muted">{t('analytics.demand.noTrend')}</div>
      ) : (
        <div style={{ display: 'grid', gap: 10 }}>
          {list.map((row) => {
            const searches = Number(row.searchCount ?? 0);
            return (
              <div key={row.day} style={{ display: 'grid', gridTemplateColumns: '120px 1fr 70px', gap: 10, alignItems: 'center' }}>
                <span className="ds-muted">{String(row.day).slice(0, 10)}</span>
                <div style={{ height: 10, background: 'var(--ds-border)', borderRadius: 6, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${Math.round((searches / max) * 100)}%`, background: 'var(--ds-primary)' }} />
                </div>
                <strong>{number(searches)}</strong>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

function DebugTable({ title, rows }: { title: string; rows?: any[] }) {
  const { t } = useTranslation();
  return (
    <Card title={title} style={{ marginTop: 12 }}>
      <Table
        size="small"
        rowKey={(row: any) => [
          title,
          row?.districtId,
          row?.cityId,
          row?.districtName,
          row?.cityName,
          row?.value,
          row?.day,
          row?.searchCount,
          row?.zeroResultCount,
          row?.nearbyFallbackCount,
          row?.activeListingCount,
        ].filter((part) => part !== undefined && part !== null).join('-')}
        dataSource={Array.isArray(rows) ? rows : []}
        columns={[
          { title: t('common.name'), render: (_: any, row: any) => row.districtName || row.cityName || row.value || row.day || t('common.notFound') },
          { title: t('analytics.columns.searches'), dataIndex: 'searchCount', render: number },
          { title: t('analytics.columns.zeroResults'), dataIndex: 'zeroResultCount', render: number },
          { title: t('analytics.demand.nearbyFallbackRate'), dataIndex: 'nearbyFallbackCount', render: number },
          { title: t('analytics.columns.contacts'), dataIndex: 'contactCompletedCount', render: number },
          { title: t('analytics.demand.supply'), dataIndex: 'activeListingCount', render: number },
        ]}
        pagination={false}
        scroll={{ x: 760 }}
      />
    </Card>
  );
}

function BreakdownPanel({ title, rows, label }: { title: string; rows?: any[]; label: string }) {
  const { t } = useTranslation();
  const list = (Array.isArray(rows) ? rows : []).slice(0, 8);
  const displayValue = (value: any) => {
    const text = cleanName(value, label);
    const normalized = text.toLowerCase();
    if (normalized === 'district') return t('analytics.demand.searchTypeDistrict');
    if (normalized === 'city') return t('analytics.demand.searchTypeCity');
    if (normalized === 'general') return t('analytics.demand.searchTypeGeneral');
    if (normalized === 'room_ad') return t('analytics.common.roomAds');
    if (normalized === 'room_wanted') return t('analytics.common.roomWanted');
    return text;
  };
  return (
    <Card title={title} style={{ flex: '1 1 320px' }}>
      {list.length === 0 ? (
        <div className="ds-muted">{t('analytics.empty')}</div>
      ) : (
        <div style={{ display: 'grid', gap: 10 }}>
          {list.map((row, index) => (
            <div key={`${title}-${row.value}-${index}`} style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
              <span>{displayValue(row.value)}</span>
              <strong>{number(row.searchCount)}</strong>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

export default function DemandIntelligencePage() {
  const { t } = useTranslation();
  const [params, setParams] = useState<GrowthAnalyticsParams>({ from: defaultFrom() });
  const normalizedParams = useMemo(() => ({
    ...params,
    from: params.from ? `${params.from}T00:00:00Z` : undefined,
    to: params.to ? `${params.to}T23:59:59Z` : undefined,
  }), [params]);
  const { data, isLoading, isFetching, isError, refetch } = useDemandIntelligence(normalizedParams);
  const summary = data?.summary ?? {};
  const kpis = data?.businessKpis ?? {};
  const riyadh = data?.riyadh ?? {};
  const insights = (Array.isArray(data?.businessInsights) ? data.businessInsights : [])
    .filter((insight: any) => !String(`${insight?.title ?? ''} ${insight?.message ?? ''}`).match(/\b(null|undefined|unknown)\b/i));

  const content = () => {
    if (isLoading) return <Spin />;
    if (isError) return <Alert type="error" showIcon message={t('analytics.demand.failedLoad')} />;
    return (
      <>
        <MetricGrid>
          <MetricCell><Stat title={t('analytics.metrics.searches')} value={number(kpis.totalSearches ?? summary.searchCount)} hint={t('analytics.demand.last7Days', { count: Number(kpis.searchesThisWeek ?? 0) })} /></MetricCell>
          <MetricCell><Stat title={t('analytics.demand.zeroResultRate')} value={pct(kpis.zeroResultRate ?? summary.zeroResultRate)} hint={t('analytics.demand.zeroResultHint')} /></MetricCell>
          <MetricCell><Stat title={t('analytics.demand.nearbyFallbackRate')} value={pct(kpis.nearbyFallbackRate ?? summary.nearbyFallbackRate)} hint={t('analytics.demand.nearbyFallbackHint')} /></MetricCell>
          <MetricCell><Stat title={t('analytics.demand.searchToContact')} value={pct(kpis.contactConversionRate ?? summary.contactConversionRate)} hint={t('analytics.demand.searchToContactHint')} /></MetricCell>
          <MetricCell><Stat title={t('analytics.demand.weeklyGrowth')} value={signedNumber(kpis.demandGrowthThisWeek)} hint={t('analytics.demand.weeklyGrowthHint')} /></MetricCell>
        </MetricGrid>

        <div className="ds-row" style={{ gap: 16, marginTop: 16 }}>
          <Card title={t('analytics.demand.highestDemand')} style={{ flex: '1 1 260px' }}>
            <strong style={{ fontSize: 20 }}>{districtName(kpis.highestDemandDistrict, t('analytics.demand.noClearLeader'))}</strong>
            <div className="ds-muted" style={{ marginTop: 6 }}>{t('analytics.demand.searchesCount', { count: Number(kpis.highestDemandDistrict?.searchCount ?? 0) })}</div>
          </Card>
          <Card title={t('analytics.demand.worstSupplyGap')} style={{ flex: '1 1 260px' }}>
            <strong style={{ fontSize: 20 }}>{districtName(kpis.worstSupplyGapDistrict, t('analytics.demand.noClearGap'))}</strong>
            <div className="ds-muted" style={{ marginTop: 6 }}>{t('analytics.demand.activeListingsCount', { count: Number(kpis.worstSupplyGapDistrict?.activeListingCount ?? 0) })}</div>
          </Card>
          <Card title={t('analytics.demand.mostSearchesFailing')} style={{ flex: '1 1 260px' }}>
            <strong style={{ fontSize: 20 }}>{districtName(kpis.highestZeroResultDistrict, t('analytics.demand.noFailingDistrict'))}</strong>
            <div className="ds-muted" style={{ marginTop: 6 }}>{t('analytics.demand.zeroResultSearchesCount', { count: Number(kpis.highestZeroResultDistrict?.zeroResultCount ?? 0) })}</div>
          </Card>
          <Card title={t('analytics.demand.bestConversion')} style={{ flex: '1 1 260px' }}>
            <strong style={{ fontSize: 20 }}>{districtName(kpis.bestConvertingDistrict, t('analytics.demand.noClearWinner'))}</strong>
            <div className="ds-muted" style={{ marginTop: 6 }}>{t('analytics.demand.searchToContactValue', { value: pct(kpis.bestConvertingDistrict?.contactConversionRate) })}</div>
          </Card>
        </div>

        <Card title={t('analytics.demand.businessInsights')} style={{ marginTop: 16 }}>
          <div className="ds-row" style={{ gap: 12 }}>
            {insights.length === 0 ? (
              <div className="ds-muted">{t('analytics.demand.noStrongInsights')}</div>
            ) : (
              insights.map((insight: any, index: number) => <InsightCard key={`${insight.title}-${index}`} insight={insight} />)
            )}
          </div>
        </Card>

        <div className="ds-row" style={{ gap: 16, marginTop: 16 }}>
          <DistrictRankCard title={t('analytics.demand.topRiyadhDemand')} rows={riyadh.topDemand} metric="demandSignals" />
          <DistrictRankCard title={t('analytics.demand.riyadhLowSupply')} rows={riyadh.lowSupply} metric="activeListingCount" emptyText={t('analytics.demand.noLowSupply')} />
        </div>

        <div className="ds-row" style={{ gap: 16, marginTop: 16 }}>
          <DistrictRankCard title={t('analytics.demand.riyadhZeroHotspots')} rows={riyadh.zeroResultHotspots} metric="zeroResultRate" renderMetric={pct} />
          <DistrictRankCard title={t('analytics.demand.riyadhFallbackHotspots')} rows={riyadh.nearbyFallbackHotspots} metric="nearbyFallbackRate" renderMetric={pct} />
        </div>

        <div className="ds-row" style={{ gap: 16, marginTop: 16 }}>
          <DistrictRankCard title={t('analytics.demand.bestRiyadhConversion')} rows={riyadh.bestContactConversion} metric="contactConversionRate" renderMetric={pct} />
          <DistrictRankCard title={t('analytics.demand.fastestGrowing')} rows={riyadh.fastestGrowing ?? data?.fastestGrowingDistricts} metric="growthSignals" renderMetric={signedNumber} />
        </div>

        <div className="ds-row" style={{ gap: 16, marginTop: 16 }}>
          <DistrictRankCard title={t('analytics.demand.mostDemandedOverall')} rows={data?.topSearchedDistricts} metric="demandSignals" />
        </div>

        <RecommendationList rows={data?.recommendations} />
        <TrendBars rows={data?.trends} />

        <div className="ds-row" style={{ gap: 16, marginTop: 16 }}>
          <BreakdownPanel title={t('analytics.demand.searchTypeDemand')} rows={data?.searchTypeDemand} label={t('analytics.demand.unclassifiedSearch')} />
          <BreakdownPanel title={t('analytics.demand.priceDemand')} rows={data?.priceRangeDemand} label={t('analytics.demand.unspecifiedPrice')} />
        </div>

        <details style={{ marginTop: 18 }}>
          <summary style={{ cursor: 'pointer', fontWeight: 700 }}>{t('analytics.demand.rawDetail')}</summary>
          <div className="ds-muted" style={{ marginTop: 8 }}>
            {t('analytics.demand.rawDetailHint')}
          </div>
          <DebugTable title={t('analytics.demand.topSearchedDistricts')} rows={data?.topSearchedDistricts} />
          <DebugTable title={t('analytics.demand.zeroResultDistricts')} rows={data?.zeroResultHotspots} />
          <DebugTable title={t('analytics.demand.demandSupplyDetail')} rows={data?.demandVsSupply} />
        </details>
      </>
    );
  };

  return (
    <div>
      <div className="ds-page-header">
        <div>
          <h1>{t('sidebar.demandIntelligence')}</h1>
          <div className="ds-page-sub">
            {t('analytics.demand.subtitle')}
          </div>
        </div>
        {isFetching && !isLoading ? <Tag color="info">{t('analytics.refreshing')}</Tag> : null}
      </div>
      <AnalyticsFilterBar params={params} setParams={setParams} onRefresh={() => refetch()} loading={isFetching} />
      {content()}
    </div>
  );
}
